import * as yup from 'yup';
import { FormManager } from 'react-form-state-manager';
import { ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ErrorBag, emailRegex } from '../../../common/helpers';
import { FindOrAddTeamInput } from '../../../__generated__/graphql';
import ContactPicker from '../Contacts/ContactPicker';
import GetTeamTypesQuery from './GetTeamTypesQuery';
import TeamPicker from './TeamPicker';
import UI from '../../../common/UI';
import useProject from '../../useProject';
import useQuery from '../../../api/useQuery';

const requiredIfPresent = (value: any) => typeof value === 'undefined' || value;

/**
 * Assumes that attributes are required when they are present.
 */
export const findOrAddTeamSchema = yup.object({
  id: yup.string().test('required', '', requiredIfPresent),
  title: yup.string().test('required', '', requiredIfPresent),
  captain: yup.object({
    id: yup.string().test('required', '', requiredIfPresent),
    email: yup.string().test('required', '', requiredIfPresent).matches(emailRegex, { name: 'email' }),
    first_name: yup.string().test('required', '', requiredIfPresent),
    last_name: yup.string().test('required', '', requiredIfPresent),
  }),
});

interface Team {
  team_type: {
    id: string;
  };
  id: string;
  title: string;
}

interface Captain {
  id: string;
  full_name: string;
  email: string;
}

export interface FindOrAddTeamFormProps {
  form: FormManager;
  formPath?: string;
  eventId: string;
  teamType?: { id: string; title?: string | null; } | null;
  team?: Team | null;
  /** The value to be set in the form when participating individually. */
  noTeamValue?: FindOrAddTeamInput | null;
  captain?: Captain | null;
  description?: ReactNode;
  errors?: ErrorBag;
  columns?: boolean;
}

const FindOrAddTeamForm = ({
  form, formPath = 'team', eventId, teamType, team: initialTeam = null, noTeamValue = null,
  captain: initialCaptain = null, description, errors, columns,
}: FindOrAddTeamFormProps) => {
  const { t } = useTranslation();
  const project = useProject();
  const nameRef = useRef(`team-${Math.round(Math.random() * 1000000)}`);

  const key = useCallback((k?: string) => `${formPath}${k ? `.${k}` : ''}`, [formPath]);

  const [team, setTeam] = useState<Team | null>(initialTeam);
  const [captain, setCaptain] = useState<Captain | null>(initialCaptain);

  const initialTeamMode = initialTeam ? 'existing' : 'none';
  const [teamMode, setTeamMode] = useState<'none' | 'existing' | 'new'>(initialTeamMode);
  const [captainMode, setCaptainMode] = useState<'existing' | 'new'>('existing');

  const initialTeamRef = useRef(initialTeam);

  const { data, loading: loadingTeamTypes } = useQuery(GetTeamTypesQuery,
    {
      variables: {
        project_id: project.id,
        event_id: eventId,
      },
      skip: teamMode !== 'new' || !!teamType?.id,
      onCompleted: (data) => {
        if (data.project.event.id === eventId && data.project.event.team_types.length > 0) {
          form.set(key('team_type.id'), data.project.event.team_types[0].id);
        } else {
          form.set(key('team_type.id'), null);
        }
      },
    },
  );

  const teamTypes = (data?.project.event.team_types || [])
    .filter(({ id }) => !teamType || id === teamType?.id);

  const reset = useCallback(() => {
    setTeamMode(initialTeamMode);
    setCaptainMode('existing');
    setTeam(initialTeam);
    setCaptain(initialCaptain);
  }, [initialTeamMode, initialTeam, initialCaptain]);

  useEffect(() => {
    // When the initial team changes (e.g., when the form is saved), reset the UI
    if (initialTeamRef.current !== initialTeam) {
      reset();
      form.reset(key());
      initialTeamRef.current = initialTeam;
    }
  }, [reset, initialTeam, form, key]);

  const participateIndividually = () => {
    reset();
    setTeamMode('none');

    form.set(key(), noTeamValue);
  };

  const selectExistingTeam = () => {
    reset();
    setTeamMode('existing');

    const value = initialTeam || noTeamValue;
    form.set(key(), value ? {
      team_type: { id: value.team_type.id },
      id: '',
    } : null);
  };

  const createNewTeam = () => {
    reset();
    setTeamMode('new');

    form.set(key(), () => ({
      team_type: teamType ? { id: teamType?.id } : null,
      title: '',
      captain: initialCaptain ? { id: initialCaptain.id } : null,
    }));
  };

  const selectTeam = (team: Team | null) => {
    setTeam(team);

    const value = team || initialTeam || noTeamValue;

    form.set(key(), value ? {
      team_type: { id: value.team_type.id },
      id: value?.id || '',
    } : null);
  };

  const selectExistingCaptain = () => {
    setCaptainMode('existing');

    form.set(key(), (values: FindOrAddTeamInput | null): FindOrAddTeamInput => ({
      ...values,
      captain: null,
    }));
  };

  const createNewCaptain = () => {
    setCaptainMode('new');

    form.set(key(), (values: FindOrAddTeamInput | null): FindOrAddTeamInput => ({
      ...values,
      captain: { email: '', first_name: '', last_name: '' },
    }));
  };

  const selectCaptain = (captain: Captain | null) => {
    setCaptain(captain);

    form.set(key(), (values: FindOrAddTeamInput | null): FindOrAddTeamInput => ({
      ...values,
      captain: captain ? { id: captain.id } : null,
    }));
  };

  return (
    <UI.FormGrid sc={{ columns: ['1fr', columns ? '1fr 1fr' : '1fr'] }}>
      <UI.InputGroup>
        <UI.InputLabel htmlFor={`${nameRef.current}-mode-no-team`}>
          {teamType?.title || t('team')}
        </UI.InputLabel>
        {description && (
          <UI.InputDescription as="div">
            {description}
          </UI.InputDescription>
        )}
        <UI.Radio
          checked={teamMode === 'none'}
          onChange={() => participateIndividually()}
          id={`${nameRef.current}-mode-no-team`}
          name={`${nameRef.current}-mode`}
        >
          {t('no_team')}
        </UI.Radio>
        <UI.Radio
          checked={teamMode === 'existing'}
          onChange={() => selectExistingTeam()}
          name={`${nameRef.current}-mode`}
        >
          {t('select_an_existing_team')}
        </UI.Radio>
        <UI.Radio
          checked={teamMode === 'new'}
          onChange={() => createNewTeam()}
          name={`${nameRef.current}-mode`}
        >
          {t('create_a_new_team')}
        </UI.Radio>
      </UI.InputGroup>

      {teamMode === 'existing' && (
        <UI.FormGrid>
          <UI.InputGroup sc={{ valid: !errors?.[key('id')], touched: form.getTouched(key('id')) }}>
            <UI.InputLabel>
              {t('existing_team')}
              {' '}
              <UI.RequiredMark />
            </UI.InputLabel>
            <TeamPicker
              team={team}
              onChange={selectTeam}
              filters={{ event: eventId, team_types: teamType ? [teamType.id] : undefined }}
              nullable={false}
            />
            <UI.ErrorMessages attribute={t('team')} errors={errors?.team} />
          </UI.InputGroup>
        </UI.FormGrid>
      )}

      {teamMode === 'new' && (
        <UI.FormGrid>
          {!teamType?.id && (
            <UI.InputGroup
              sc={{
                valid: !errors?.[key('team_type')],
                touched: form.getTouched(key('team_type')),
                relative: true,
              }}
            >
              <UI.InputLabel htmlFor={`${nameRef.current}-team-type`}>
                {t('team_type')}
                {' '}
                <UI.RequiredMark />
              </UI.InputLabel>
              {loadingTeamTypes && (
                <UI.InputFallback />
              )}
              {teamTypes.length > 0 && !loadingTeamTypes && (
                <UI.Select
                  {...form.select(
                    key('team_type.id'),
                    teamTypes.map((teamType) => teamType.id),
                  )}
                  id={`${nameRef.current}-team-type`}
                >
                  {teamTypes.map((teamTypes, index) => (
                    <option {...form.option(index)} key={index}>
                      {teamTypes.title || t('team')}
                    </option>
                  ))}
                </UI.Select>
              )}
              {teamTypes.length === 0 && !loadingTeamTypes && (
                <UI.Warning>
                  {t('no_team_types_added_yet')}
                  {' '}
                  <UI.Link to={`/${project.id}/events/${eventId}/settings/teams`}>
                    {t('manage_team_types')}
                  </UI.Link>
                </UI.Warning>
              )}
            </UI.InputGroup>
          )}

          <UI.InputGroup sc={{ valid: !errors?.[key('title')], touched: form.getTouched(key('title')) }}>
            <UI.InputLabel htmlFor={`${nameRef.current}-team-name`}>
              {t('team_name')}
              {' '}
              <UI.RequiredMark />
            </UI.InputLabel>
            <UI.DebouncedInput {...form.text(key('title'))} id={`${nameRef.current}-team-name`} />
            <UI.ErrorMessages attribute={t('team_name')} errors={errors?.[key('title')]} />
          </UI.InputGroup>

          {!initialCaptain && (
            <>
              <UI.InputGroup>
                <UI.Radio
                  checked={captainMode === 'existing'}
                  onChange={() => selectExistingCaptain()}
                >
                  {t('select_an_existing_captain')}
                </UI.Radio>
                <UI.Radio
                  checked={captainMode === 'new'}
                  onChange={() => createNewCaptain()}
                >
                  {t('create_a_new_captain')}
                </UI.Radio>
              </UI.InputGroup>

              {captainMode === 'new' && (
                <>
                  <UI.FormGrid sc={{ columns: ['1fr', '1fr 1fr'] }}>
                    <UI.InputGroup
                      sc={{
                        valid: !errors?.[key('captain.first_name')],
                        touched: form.getTouched(key('captain.first_name')),
                      }}
                    >
                      <UI.InputLabel htmlFor={`${nameRef.current}-captain-first-name`}>
                        {t('first_name')}
                        {' '}
                        <UI.RequiredMark />
                      </UI.InputLabel>
                      <UI.DebouncedInput {...form.text(key('captain.first_name'))} id={`${nameRef.current}-captain-first-name`} />
                      <UI.ErrorMessages attribute={t('first_name')} errors={errors?.[key('captain.first_name')]} />
                    </UI.InputGroup>
                    <UI.InputGroup
                      sc={{
                        valid: !errors?.[key('captain.last_name')], touched: form.getTouched(key('captain.last_name')),
                      }}
                    >
                      <UI.InputLabel htmlFor={`${nameRef.current}-captain-last-name`}>
                        {t('last_name')}
                        {' '}
                        <UI.RequiredMark />
                      </UI.InputLabel>
                      <UI.DebouncedInput {...form.text(key('captain.last_name'))} id={`${nameRef.current}-captain-last-name`} />
                      <UI.ErrorMessages attribute={t('last_name')} errors={errors?.[key('captain.last_name')]} />
                    </UI.InputGroup>
                  </UI.FormGrid>
                  <UI.InputGroup
                    sc={{ valid: !errors?.[key('captain.email')], touched: form.getTouched(key('captain.email')) }}
                  >
                    <UI.InputLabel htmlFor={`${nameRef.current}-captain-email`}>
                      {t('email_address')}
                      {' '}
                      <UI.RequiredMark />
                    </UI.InputLabel>
                    <UI.DebouncedInput {...form.text(key('captain.email'))} id={`${nameRef.current}-captain-email`} />
                    <UI.ErrorMessages attribute={t('email')} errors={errors?.[key('captain.email')]} />
                  </UI.InputGroup>
                </>
              )}

              {captainMode === 'existing' && (
                <UI.InputGroup
                  sc={{ valid: !errors?.[key('captain')], touched: form.getTouched(key('captain')) }}
                >
                  <UI.InputLabel>
                    {t('captain')}
                    {' '}
                    <UI.RequiredMark />
                  </UI.InputLabel>
                  <ContactPicker participant={captain} onChange={selectCaptain} nullable={false} />
                  <UI.ErrorMessages attribute={t('captain')} errors={errors?.[key('captain')]} />
                </UI.InputGroup>
              )}
            </>
          )}
        </UI.FormGrid>
      )}
    </UI.FormGrid>
  );
};

export default FindOrAddTeamForm;
